import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, AlertTriangle, ReceiptText, FileSpreadsheet, FileText } from 'lucide-react';
import { formatCents, type PrOverview, type PrMatrix, type InvoicingPeriod } from '@mdu-invoicing/shared';
import { apiFetch, apiDownload, ApiError } from '@/lib/api-client';
import { Card } from '@/components/ui/Card';

interface PrResponse {
  period: InvoicingPeriod;
  overview: PrOverview;
  matrix: PrMatrix;
}

export function Pr() {
  const [data, setData] = useState<PrResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<null | 'xlsx' | 'pdf'>(null);

  useEffect(() => {
    let cancelled = false;
    apiFetch<PrResponse>('/pr')
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof ApiError ? e.message : 'Erreur de chargement');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleExport(format: 'xlsx' | 'pdf') {
    if (!data) return;
    setDownloading(format);
    try {
      // Carry the loaded period so the file matches exactly what is shown on screen.
      const { from, to } = data.period;
      const { blob, filename } = await apiDownload(`/pr/export?format=${format}&from=${from}&to=${to}`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Export ${format.toUpperCase()} généré`);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Échec de la génération du fichier');
    } finally {
      setDownloading(null);
    }
  }

  if (loading) {
    return (
      <Card className="flex items-center gap-2 p-5 text-sm text-slate-400">
        <Loader2 className="h-4 w-4 animate-spin" /> Calcul de l'aperçu des PR…
      </Card>
    );
  }
  if (error || !data) {
    return <Card className="p-5 text-sm text-rose-700">Impossible de charger l'aperçu : {error ?? '—'}.</Card>;
  }

  const { period, overview, matrix } = data;
  const { totals, unpriced } = overview;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-500">
          Totaux SSV / TSA par zone de déploiement pour la période{' '}
          <span className="font-semibold text-slate-700">
            {period.from} → {period.to}
          </span>
          . Création des PR dans AFAS hors périmètre de l'outil.
        </p>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() => handleExport('xlsx')}
            disabled={downloading !== null || matrix.rows.length === 0}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {downloading === 'xlsx' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileSpreadsheet className="h-4 w-4 text-emerald-600" />
            )}
            XLS
          </button>
          <button
            type="button"
            onClick={() => handleExport('pdf')}
            disabled={downloading !== null || matrix.rows.length === 0}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {downloading === 'pdf' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileText className="h-4 w-4 text-rose-600" />
            )}
            PDF
          </button>
        </div>
      </div>

      {/* Grand totals */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <SummaryCard label="Total SSV" count={totals.ssv.count} cents={totals.ssv.totalCents} />
        <SummaryCard label="Total TSA" count={totals.tsa.count} cents={totals.tsa.totalCents} />
        <SummaryCard label="Total général" count={totals.ssv.count + totals.tsa.count} cents={totals.totalCents} highlight />
        <Card className="p-4">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-orange/10 text-brand-orange">
            <ReceiptText className="h-5 w-5" />
          </div>
          <div className="mt-3 text-2xl font-extrabold tabular-nums text-slate-900">{totals.areaCount}</div>
          <div className="text-xs font-medium text-slate-500">Zones de déploiement</div>
        </Card>
      </div>

      {/* PR matrix: Deployment Area × service-type (Gerd round-2 §F) */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full min-w-[760px] text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3">Zone de déploiement</th>
                {matrix.columns.map((c) => (
                  <th
                    key={c.key}
                    className="px-3 py-3 text-right"
                    title={c.serviceType ?? 'Réservé — E/QE facturé au tarif QC (comptabilisé dans la colonne QC)'}
                  >
                    {c.label}
                  </th>
                ))}
                <th className="px-4 py-3 text-right">Total (€)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {matrix.rows.map((row) => (
                <tr key={row.deploymentArea} className="hover:bg-slate-50/60">
                  <td className="whitespace-nowrap px-4 py-2.5 font-semibold text-slate-800">{row.deploymentArea}</td>
                  {matrix.columns.map((c) => {
                    const cell = row.cells[c.key];
                    return (
                      <td
                        key={c.key}
                        className="px-3 py-2.5 text-right tabular-nums text-slate-600"
                        title={cell.cents != null && cell.count > 0 ? formatCents(cell.cents) : undefined}
                      >
                        {cell.count > 0 ? cell.count : <span className="text-slate-300">·</span>}
                      </td>
                    );
                  })}
                  <td className="px-4 py-2.5 text-right font-bold tabular-nums text-slate-900">
                    {formatCents(row.totalCents)}
                  </td>
                </tr>
              ))}
              {matrix.rows.length === 0 && (
                <tr>
                  <td colSpan={matrix.columns.length + 2} className="px-4 py-12 text-center text-sm text-slate-400">
                    Aucun enregistrement tarifé pour cette période.
                  </td>
                </tr>
              )}
            </tbody>
            {matrix.rows.length > 0 && (
              <tfoot>
                <tr className="border-t border-slate-200 bg-slate-50 text-sm font-bold text-slate-900">
                  <td className="px-4 py-3">Total</td>
                  {matrix.columns.map((c) => (
                    <td key={c.key} className="px-3 py-3 text-right tabular-nums">
                      {matrix.totals.cells[c.key].count || ''}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-right tabular-nums gradient-text">{formatCents(matrix.totals.totalCents)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Unpriced (§4 gap) */}
      {unpriced.count > 0 && (
        <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            <span className="font-semibold">{unpriced.count} enregistrement(s) non tarifé(s)</span> exclus des totaux
            (Design Quadrant hors §4 ou contractant hors périmètre).{' '}
            {unpriced.deploymentAreas.length > 0 && (
              <>Zones concernées : {unpriced.deploymentAreas.join(', ')}.</>
            )}
          </p>
        </div>
      )}
    </div>
  );
}

function SummaryCard({
  label,
  count,
  cents,
  highlight = false,
}: {
  label: string;
  count: number;
  cents: number;
  highlight?: boolean;
}) {
  return (
    <Card className="p-4">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</div>
      <div
        className={
          'mt-1 text-2xl font-extrabold tabular-nums ' + (highlight ? 'gradient-text' : 'text-slate-900')
        }
      >
        {formatCents(cents)}
      </div>
      <div className="text-xs font-medium text-slate-500">{count} enregistrement(s)</div>
    </Card>
  );
}
